/**
 * Smart Material Spec Auto-Parsing Engine
 * 
 * Bóc tách quy cách tự động từ tên vật tư tự do trong ngành in ấn & bao bì.
 * Ví dụ: "Giấy Ivory 350gsm 79x109cm" -> { subType: "Giấy", gsm: 350, length: 1090, width: 790, ... }
 */

export interface ParsedMaterialSpecs {
  subType?: string;
  typeId?: string;
  typeLabel?: string;
  gsm?: number;
  length?: number;
  width?: number;
  height?: number;
  specs?: string;
  raw: string;
}

export interface MaterialTypeOption {
  id: string;
  label?: string;
  code?: string;
  group?: string;
  gsm?: string | number;
}

export function parseMaterialSpecs(
  rawName: string,
  existingTypes?: MaterialTypeOption[]
): ParsedMaterialSpecs {
  if (!rawName || typeof rawName !== "string") {
    return { raw: rawName || "" };
  }

  const text = rawName.trim();
  const result: ParsedMaterialSpecs = { raw: text };

  // 1. GSM Extraction (e.g. 350gsm, 350 g/m2, 250 gsm, 300g)
  const gsmRegex = /(?:^|\s|_|[-/])(\d{2,4})\s*(?:gsm|g\/m2|g\b)/i;
  const gsmMatch = text.match(gsmRegex);
  if (gsmMatch && gsmMatch[1]) {
    const parsedGsm = parseInt(gsmMatch[1], 10);
    if (parsedGsm >= 30 && parsedGsm <= 1500) {
      result.gsm = parsedGsm;
    }
  }

  // Fallback GSM check: e.g. "Ivory 300", "Couche 150", "C250", "D350"
  if (!result.gsm) {
    const fallbackGsmRegex = /(?:Ivory|Couche|Duplex|Kraft|Ford|Bristol|C|D|F)\s*(\d{2,3})\b/i;
    const fbMatch = text.match(fallbackGsmRegex);
    if (fbMatch && fbMatch[1]) {
      const parsedGsm = parseInt(fbMatch[1], 10);
      if (parsedGsm >= 50 && parsedGsm <= 600) {
        result.gsm = parsedGsm;
      }
    }
  }

  // 2. Roll / Cuộn Width (e.g. "cuộn 120cm", "cuộn 1200mm", "khổ 65cm", "cuon 1000")
  const rollRegex = /(?:cuộn|cuon|khổ|kho)\s*(\d{2,4})\s*(cm|mm)?/i;
  const rollMatch = text.match(rollRegex);
  if (rollMatch && rollMatch[1]) {
    let rollSize = parseInt(rollMatch[1], 10);
    const unit = (rollMatch[2] || "").toLowerCase();
    if (unit === "cm" || (unit === "" && rollSize < 200)) {
      rollSize *= 10;
    }
    result.width = rollSize;
    result.length = 0; // Đánh dấu dạng cuộn liên tục
    if (!result.specs) {
      result.specs = `Khổ cuộn ${rollSize}mm`;
    }
  }

  // 3. Dimensions DxRxC (e.g. 790x1090, 790*1090, 790 × 1090, 79x109, 79x109cm)
  if (!result.width) {
    const dimRegex = /(?:^|\s|_|[-/])(?:khổ\s*)?(\d{2,4})\s*[*xX×]\s*(\d{2,4})(?:\s*[*xX×]\s*(\d{1,3}))?\s*(mm|cm)?/i;
    const dimMatch = text.match(dimRegex);
    if (dimMatch) {
      let d1 = parseInt(dimMatch[1], 10);
      let d2 = parseInt(dimMatch[2], 10);
      let d3 = dimMatch[3] ? parseInt(dimMatch[3], 10) : undefined;
      const unit = (dimMatch[4] || "").toLowerCase();

      // Nếu đơn vị là cm hoặc cả 2 số đều < 200 (ví dụ 79x109, 65x86) -> quy đổi sang mm bằng cách nhân 10
      if (unit === "cm" || (unit === "" && d1 < 200 && d2 < 200)) {
        d1 *= 10;
        d2 *= 10;
        if (d3 && d3 < 200) d3 *= 10;
      }

      // Chuẩn ngành in: Chiều dài (length) là kích thước lớn hơn, Chiều rộng (width) là kích thước nhỏ hơn
      result.length = Math.max(d1, d2);
      result.width = Math.min(d1, d2);
      if (d3) {
        result.height = d3;
      }
    }
  }

  // 4. Subtype & Material Type Auto-Matching
  const lowerText = text.toLowerCase();
  let detectedGroup = "";
  let matchedKeyword = "";

  if (lowerText.includes("ivory")) {
    result.subType = "Giấy";
    matchedKeyword = "ivory";
  } else if (lowerText.includes("couche") || lowerText.match(/\bc\d{2,3}\b/i)) {
    result.subType = "Giấy";
    matchedKeyword = "couche";
  } else if (lowerText.includes("duplex")) {
    result.subType = "Giấy";
    matchedKeyword = "duplex";
  } else if (lowerText.includes("kraft")) {
    result.subType = "Giấy";
    matchedKeyword = "kraft";
  } else if (lowerText.includes("ford") || lowerText.includes("fort")) {
    result.subType = "Giấy";
    matchedKeyword = "ford";
  } else if (lowerText.includes("bristol")) {
    result.subType = "Giấy";
    matchedKeyword = "bristol";
  } else if (lowerText.includes("decal")) {
    result.subType = "Decal";
    matchedKeyword = "decal";
  } else if (
    lowerText.includes("bopp") ||
    lowerText.includes("màng") ||
    lowerText.includes("mang") ||
    lowerText.includes("pe") ||
    lowerText.includes("pet")
  ) {
    result.subType = "Màng";
    matchedKeyword = "bopp";
  } else if (lowerText.includes("mực") || lowerText.includes("muc")) {
    result.subType = "Mực";
    matchedKeyword = "muc";
  } else if (lowerText.includes("băng keo") || lowerText.includes("bang keo")) {
    result.subType = "Phụ liệu";
  } else if (lowerText.includes("giấy") || lowerText.includes("giay")) {
    result.subType = "Giấy";
  }

  // 5. If existingTypes lookup available, attempt matching typeId
  if (existingTypes && existingTypes.length > 0 && matchedKeyword) {
    // Ưu tiên 1: Khớp cả keyword và GSM (ví dụ IVORY_350 hoặc label chứa cả Ivory và 350)
    if (result.gsm) {
      const exactMatch = existingTypes.find((t) => {
        const code = (t.code || "").toLowerCase();
        const label = (t.label || "").toLowerCase();
        const hasKeyword = code.includes(matchedKeyword) || label.includes(matchedKeyword);
        const hasGsm =
          String(t.gsm) === String(result.gsm) ||
          code.includes(String(result.gsm)) ||
          label.includes(String(result.gsm));
        return hasKeyword && hasGsm;
      });

      if (exactMatch) {
        result.typeId = exactMatch.id;
        result.typeLabel = exactMatch.label || exactMatch.code;
      }
    }

    // Ưu tiên 2: Khớp keyword chính (nếu chưa tìm thấy theo GSM)
    if (!result.typeId) {
      const keywordMatch = existingTypes.find((t) => {
        const code = (t.code || "").toLowerCase();
        const label = (t.label || "").toLowerCase();
        return code.includes(matchedKeyword) || label.includes(matchedKeyword);
      });

      if (keywordMatch) {
        result.typeId = keywordMatch.id;
        result.typeLabel = keywordMatch.label || keywordMatch.code;
      }
    }
  }

  return result;
}
